import React, { useState } from 'react';
import { ArrowUpDown, Plus, Trash2, Check, X } from 'lucide-react';
import { SortRule } from '../types';

interface MultiSortModalProps {
  isOpen: boolean;
  onClose: () => void;
  sortRules: SortRule[];
  onApplySort: (rules: SortRule[]) => void;
  fieldOptions?: { id: string; label: string }[];
}

const DEFAULT_SORTABLE_FIELDS = [
  { id: 'estimatedDealValue', label: 'Estimated Deal Value ($)' },
  { id: 'company', label: 'Company Name (consignee)' },
  { id: 'name', label: 'Lead Name / Account' },
  { id: 'contactPerson', label: 'Primary Contact Person' },
  { id: 'owner', label: 'Salesperson Ownership' },
  { id: 'serviceTypes', label: 'Service Types' },
  { id: 'industries', label: 'Industries' },
  { id: 'outboundStates', label: 'Outbound States' },
  { id: 'outboundWarehouses', label: 'Outbound Warehouses' },
  { id: 'daysOfService', label: 'Days of Service' },
  { id: 'lastContacted', label: 'Last Update Date' },
  { id: 'status', label: 'Status' },
  { id: 'followUpDate', label: 'Follow-up Date' },
];

export default function MultiSortModal({
  isOpen,
  onClose,
  sortRules,
  onApplySort,
  fieldOptions,
}: MultiSortModalProps) {
  const fields = fieldOptions && fieldOptions.length > 0 ? fieldOptions : DEFAULT_SORTABLE_FIELDS;
  const [rules, setRules] = useState<SortRule[]>(
    sortRules.length > 0
      ? sortRules
      : [
          { field: fields[0]?.id || 'dealValue', direction: 'desc' },
        ]
  );

  if (!isOpen) return null;

  const addSortRule = () => {
    setRules([...rules, { field: 'proposalDate', direction: 'desc' }]);
  };

  const removeSortRule = (idx: number) => {
    setRules(rules.filter((_, i) => i !== idx));
  };

  const updateSortRule = (
    idx: number,
    key: keyof SortRule,
    val: any
  ) => {
    setRules(
      rules.map((r, i) => (i === idx ? { ...r, [key]: val } : r))
    );
  };

  const handleApply = () => {
    onApplySort(rules);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-md overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900/80">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">
              Multi-Level Sort Menu
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex-1 overflow-y-auto max-h-[60vh] space-y-3">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            Prioritize primary parameters (e.g. sort by Deal Value first), followed by secondary parameters.
          </p>

          {rules.map((rule, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60"
            >
              <span className="text-[10px] font-extrabold text-blue-600 dark:text-blue-400 uppercase tracking-widest min-w-[50px]">
                {idx === 0 ? 'SORT BY' : 'THEN BY'}
              </span>

              <select
                value={rule.field}
                onChange={(e) => updateSortRule(idx, 'field', e.target.value)}
                className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 flex-1"
              >
                {fields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>

              <select
                value={rule.direction}
                onChange={(e) =>
                  updateSortRule(
                    idx,
                    'direction',
                    e.target.value as 'asc' | 'desc'
                  )
                }
                className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 min-w-[90px]"
              >
                <option value="desc">Desc (↓)</option>
                <option value="asc">Asc (↑)</option>
              </select>

              <button
                type="button"
                onClick={() => removeSortRule(idx)}
                className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded transition cursor-pointer"
                title="Remove Level"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addSortRule}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 transition cursor-pointer border border-blue-200 dark:border-blue-900"
          >
            <Plus className="w-3.5 h-3.5" /> Add Priority Level
          </button>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition cursor-pointer"
          >
            <Check className="w-3.5 h-3.5" /> Apply Multi-Sort
          </button>
        </div>
      </div>
    </div>
  );
}
